import {
  ActionIcon,
  Badge,
  CopyButton,
  Group,
  Paper,
  Radio,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconCheck, IconCopy, IconExternalLink } from '@tabler/icons-react'
import type { ReactNode, Ref } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import type { AccessType } from '@/services/api/api.types'

import { isValidPaymentUrl } from './accessSettings'
import { copy } from './copy'

interface SettingCardProps {
  title: string
  description: ReactNode
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  /** Smaller title, for cards inside a popup. */
  compact?: boolean
  children?: ReactNode
}

/** A bordered card with a title and description, and a switch when it controls a setting. */
export function SettingCard({ title, description, checked, onChange, disabled, compact, children }: SettingCardProps) {
  const isMobile = useCheckMobileScreen()
  return (
    <Paper component="section" aria-label={title} radius="md" p={isMobile || compact ? 'md' : 'lg'} withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            {compact ? <Text fw={700}>{title}</Text> : <Title order={isMobile ? 6 : 5}>{title}</Title>}
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          </Stack>
          {onChange && (
            <Switch
              checked={!!checked}
              onChange={e => onChange(e.currentTarget.checked)}
              disabled={disabled}
              aria-label={title}
              size="md"
            />
          )}
        </Group>
        {children}
      </Stack>
    </Paper>
  )
}

export function ComingSoonLabel({ label }: { label: string }) {
  return (
    <Group gap="xs" component="span">
      {label}
      <Badge variant="light" color="gray" tt="none" size="sm">
        {copy.comingSoon}
      </Badge>
    </Group>
  )
}

interface AccessTypeRadiosProps {
  kind: 'invite' | 'selfReg'
  label: string
  value: AccessType
  onChange: (value: AccessType) => void
  isAvailable: (type: AccessType) => boolean
  disabled?: boolean
}

export function AccessTypeRadios({ kind, label, value, onChange, isAvailable, disabled }: AccessTypeRadiosProps) {
  const paidUnavailable = kind === 'selfReg' && !isAvailable('channel')
  return (
    <Radio.Group value={value} onChange={next => onChange(next as AccessType)} aria-label={label}>
      <Stack gap="md">
        <Radio
          value="channel"
          label={copy.accessTypes.channel.label}
          description={paidUnavailable ? copy.selfReg.paidUnavailable : copy.accessTypes.channel[kind]}
          disabled={disabled || !isAvailable('channel')}
        />
        <Radio
          value="trial"
          label={copy.accessTypes.trial.label}
          description={copy.accessTypes.trial[kind]}
          disabled={disabled || !isAvailable('trial')}
        />
        <Radio
          value="paying"
          label={<ComingSoonLabel label={copy.accessTypes.paying.label} />}
          description={copy.accessTypes.paying[kind]}
          disabled
        />
      </Stack>
    </Radio.Group>
  )
}

interface LinkFieldProps {
  value: string
  label: string
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  error?: ReactNode
  disabled?: boolean
  inputRef?: Ref<HTMLInputElement>
}

/** A URL with copy and open buttons. Read-only unless it has an onChange. */
export function LinkField({ value, label, onChange, onBlur, placeholder, error, disabled, inputRef }: LinkFieldProps) {
  const canOpen = isValidPaymentUrl(value)
  return (
    <Group gap="xs" maw={560} align="flex-start" wrap="nowrap">
      <TextInput
        ref={inputRef}
        aria-label={label}
        value={value}
        readOnly={!onChange}
        onChange={onChange ? e => onChange(e.currentTarget.value) : undefined}
        onBlur={onBlur}
        placeholder={placeholder}
        error={error}
        disabled={disabled}
        flex={1}
        size="sm"
        styles={{ input: { fontFamily: 'monospace' } }}
      />
      <CopyButton value={value} timeout={2000}>
        {({ copied, copy: copyValue }) => (
          <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
            <ActionIcon
              color={copied ? 'teal' : 'gray'}
              variant="subtle"
              onClick={copyValue}
              size="lg"
              disabled={!value}
              aria-label={copied ? `${label} copied` : `Copy ${label}`}>
              {copied ? <IconCheck size={18} aria-hidden="true" /> : <IconCopy size={18} aria-hidden="true" />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
      <Tooltip label="Open in new tab" withArrow>
        <ActionIcon
          component="a"
          href={canOpen ? value.trim() : undefined}
          target="_blank"
          rel="noopener noreferrer"
          color="gray"
          variant="subtle"
          size="lg"
          disabled={!canOpen}
          aria-label={`Open ${label} in new tab`}>
          <IconExternalLink size={18} aria-hidden="true" />
        </ActionIcon>
      </Tooltip>
    </Group>
  )
}
